import React, { useState } from 'react'
import ConfirmModal from 'components/ConfirmationModal'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { useFormik } from 'formik'
import Autocomplete from '@material-ui/lab/Autocomplete'
import moment from 'moment-timezone'
import { useDispatch } from 'react-redux'
import { toast } from 'utils/notify'

import { updateConferenceById, unarchiveConference } from 'store/conference'
import FormTextField from 'components/Form/FormTextField'
import CustomDateTimePicker from 'components/CustomDateTimePicker'
import ProgressButton from 'components/ProgressButton'
import * as yup from 'yup'
import { Roles } from 'constants/index'

const timeZonesOptions = moment.tz.names()

const validationSchema = yup.object().shape({
  confName: yup.string().label('Conference Name').trim().required('Conference Name is required.'),
  confCode: yup.string().label('Conference Code').trim().required('Conference Code is required'),
  timezone: yup.string().label('Conference Timezone').required('Conference Timezone is required'),
  startDate: yup.date().label('Start Date').required().typeError('Invalid date'),
  endDate: yup
    .date()
    .label('End date')
    .typeError('Invalid date')
    .test('Date Check', 'End date must be same or after start date.', function (value) {
      const startDate = moment(this.parent.startDate)
      const endDate = moment(value)
      return endDate.isSameOrAfter(startDate, 'date')
    })
    .required(),
  viewableUntil: yup
    .date()
    .label('Viewable Until Date')
    .typeError('Invalid date')
    .test('Viewable Until date must be same or after end date.', function (value) {
      const endDate = moment(this.parent.endDate)
      const viewableDate = moment(value)
      return viewableDate.isSameOrAfter(endDate, 'date')
    })
    .required(),
  is24hrFormat: yup.bool().label('Use 24hr Format').required(),
  abstractPrefix: yup
    .string()
    .min(2)
    .max(3)
    .matches(/^[a-zA-Z]+$/, 'Abstract Number Prefix can only contain letters.')
    .label('Abstract Prefix')
    .trim()
    .optional(),
  posterPrefix: yup
    .string()
    .min(2)
    .max(3)
    .matches(/^[a-zA-Z]+$/, 'Poster Number Prefix can only contain letters.')
    .label('Poster Session Prefix')
    .trim()
    .optional()
})

const BasicInformationForm = ({
  conference,
  conferences,
  defaultTimezone,
  user,
  isCreateMode = false,
  onSubmit: externalSubmit
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const isArchived =
    conference?.isArchived &&
    conference?.viewableUntil &&
    moment(conference.viewableUntil).isBefore(moment(), 'day')
  const isOrganizerOwned = user?.role === Roles.ORGANIZER && conference?.organizerId === user?.id
  const isReadOnly = isArchived && user?.role !== Roles.SUPER_ADMIN && !isOrganizerOwned
  const [unarchiveModalOpen, setUnarchiveModalOpen] = useState(false)
  const [unarchiveModalLoading, setUnarchiveModalLoading] = useState(false)
  const [pendingPayload, setPendingPayload] = useState(null)

  const initialValues = {
    confName: conference?.confName ?? '',
    confCode: conference?.confCode ?? '',
    timezone: conference?.timezone ?? '',
    startDate: conference?.startDate
      ? moment(conference?.startDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    endDate: conference?.endDate
      ? moment(conference?.endDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    viewableUntil:
      conference?.viewableUntil && conference?.timezone
        ? moment(conference?.viewableUntil).tz(conference?.timezone)
        : conference?.endDate && conference?.timezone
        ? moment(conference?.endDate).tz(conference?.timezone)
        : moment().tz(defaultTimezone),
    is24hrFormat: conference?.is24hrFormat ?? false,
    abstractPrefix: conference?.abstractPrefix ?? '',
    posterPrefix: conference?.posterPrefix ?? ''
  }

  const buildPayload = (values) => ({
    startDate:
      typeof values.startDate === 'number'
        ? values.startDate
        : values.startDate.startOf('day').valueOf(),
    endDate:
      typeof values.endDate === 'number' ? values.endDate : values.endDate.endOf('day').valueOf(),
    viewableUntil:
      typeof values.viewableUntil === 'number'
        ? values.viewableUntil
        : values.viewableUntil.endOf('day').valueOf(),
    confCode: values.confCode.trim(),
    timezone: values.timezone.trim(),
    confName: values.confName.trim(),
    is24hrFormat: values.is24hrFormat,
    abstractPrefix: values.abstractPrefix.toUpperCase(),
    posterPrefix: values.posterPrefix.toUpperCase()
  })

  const performUpdate = async (payload) => {
    setLoading(true)
    await dispatch(updateConferenceById(conference.id, payload))
    toast.success('Basic information updated.')
    setLoading(false)
  }

  const handleUnarchiveConfirm = async () => {
    if (!pendingPayload) return
    try {
      setUnarchiveModalLoading(true)
      await dispatch(unarchiveConference(conference.id))
      await performUpdate(pendingPayload)
    } catch {
      toast.error('Could not unarchive conference. Try again.')
    } finally {
      setUnarchiveModalLoading(false)
      setUnarchiveModalOpen(false)
      setPendingPayload(null)
    }
  }

  const handleSubmit = async (values) => {
    if (isCreateMode && externalSubmit) {
      externalSubmit(values)
      return
    }

    if (!conference?.id) {
      toast.error('Conference ID is missing. Reload and try again.')
      return
    }

    const otherConferences = conferences.filter((row) => row.id !== conference.id)
    const sameConf = otherConferences.find((row) => row.confCode === values.confCode)
    if (sameConf) {
      toast.warning('A conference with this code already exists.')
      return
    }

    const payload = buildPayload(values)

    const isCurrentlyArchived =
      conference?.isArchived &&
      conference?.viewableUntil &&
      moment(conference.viewableUntil).isBefore(moment(), 'day')
    const newViewableUntilIsFuture = moment(payload.viewableUntil).isSameOrAfter(moment(), 'day')

    if (isCurrentlyArchived && newViewableUntilIsFuture) {
      setPendingPayload(payload)
      setUnarchiveModalOpen(true)
      return
    }

    try {
      await performUpdate(payload)
    } catch (error) {
      toast.error('Could not update basic information.')
      console.error(error)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true
  })

  return (
    <Paper className={classes.section}>
      <ConfirmModal
        open={unarchiveModalOpen}
        dialogTitle="Unarchive Conference"
        dialogText="Updating the Viewable Until date to a future date will unarchive this conference and reactivate all conference admin/owner accounts."
        dialogText2="Do you want to proceed?"
        closeModal={() => {
          setUnarchiveModalOpen(false)
          setPendingPayload(null)
        }}
        handleConfirm={handleUnarchiveConfirm}
        inProgress={unarchiveModalLoading}
      />
      <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
        Basic Information
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        {isReadOnly && (
          <Typography variant="body2" style={{ marginBottom: 12, color: '#d32f2f' }}>
            This conference is archived. Only a Super Admin can edit its basic information.
          </Typography>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormTextField
              label="Conference Name"
              name="confName"
              value={formik.values.confName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.confName && formik.errors.confName)}
              helperText={
                formik.touched.confName && formik.errors.confName ? formik.errors.confName : ''
              }
              fullWidth
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Conference Code"
              name="confCode"
              value={formik.values.confCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.confCode && formik.errors.confCode)}
              helperText={
                formik.touched.confCode && formik.errors.confCode ? formik.errors.confCode : ''
              }
              fullWidth
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              disableClearable
              disabled={isReadOnly}
              options={timeZonesOptions}
              getOptionLabel={(option) => option}
              value={formik.values.timezone}
              onChange={(event, value) => {
                if (value) {
                  formik.setFieldValue('timezone', value)
                  formik.setFieldTouched('timezone', true)
                  formik.setFieldValue('startDate', moment(formik.values.startDate).tz(value))
                  formik.setFieldValue('endDate', moment(formik.values.endDate).tz(value))
                  formik.setFieldValue(
                    'viewableUntil',
                    moment(formik.values.viewableUntil).tz(value)
                  )
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="timezone"
                  error={!!(formik.touched.timezone && formik.errors.timezone)}
                  helperText={
                    formik.touched.timezone && formik.errors.timezone ? formik.errors.timezone : ''
                  }
                  label="Timezone"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField
              label="Abstract Number Prefix"
              name="abstractPrefix"
              value={formik.values.abstractPrefix}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.abstractPrefix && formik.errors.abstractPrefix)}
              helperText={
                formik.touched.abstractPrefix && formik.errors.abstractPrefix
                  ? formik.errors.abstractPrefix
                  : ''
              }
              fullWidth
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Poster Number Prefix"
              name="posterPrefix"
              value={formik.values.posterPrefix}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.posterPrefix && formik.errors.posterPrefix)}
              helperText={
                formik.touched.posterPrefix && formik.errors.posterPrefix
                  ? formik.errors.posterPrefix
                  : ''
              }
              fullWidth
              disabled={isReadOnly}
            />
          </Grid>

          <Grid item xs={12} style={{ paddingTop: '0', paddingBottom: '0' }}>
            <Typography variant="subtitle1" color="secondary" className={classes.subheading}>
              Conference Dates
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <CustomDateTimePicker
              mode="date"
              disablePast
              disabled={isReadOnly}
              value={formik.values.startDate}
              label="Start Date"
              name="startDate"
              onChange={(value) => {
                setTimeout(() => {
                  formik.setFieldTouched('startDate', true, false)
                  formik.setFieldValue('startDate', value, false)
                  // Trigger validation manually after update
                  formik.validateForm()
                }, 0)
              }}
              error={!!(formik.touched.startDate && formik.errors.startDate)}
              helperText={
                formik.touched.startDate && formik.errors.startDate ? formik.errors.startDate : ''
              }
              onBlur={() => formik.setFieldTouched('startDate', true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomDateTimePicker
              mode="date"
              disablePast
              disabled={isReadOnly}
              value={formik.values.endDate}
              label="End Date"
              name="endDate"
              minDate={formik.values.startDate}
              onChange={(value) => {
                // Use setTimeout to batch the updates and then validate
                setTimeout(() => {
                  formik.setFieldTouched('endDate', true, false)
                  formik.setFieldValue('endDate', value, false)
                  formik.setFieldValue('viewableUntil', value, false)
                  // Trigger validation manually after all updates
                  formik.validateForm()
                }, 0)
              }}
              error={!!(formik.touched.endDate && formik.errors.endDate)}
              helperText={
                formik.touched.endDate && formik.errors.endDate ? formik.errors.endDate : ''
              }
              onBlur={() => formik.setFieldTouched('endDate', true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomDateTimePicker
              mode="date"
              disablePast
              disabled={isReadOnly}
              value={formik.values.viewableUntil}
              label="Viewable Until"
              name="viewableUntil"
              minDate={formik.values.endDate}
              onChange={(value) => {
                setTimeout(() => {
                  formik.setFieldTouched('viewableUntil', true, false)
                  formik.setFieldValue('viewableUntil', value, false)
                  // Trigger validation manually after update
                  formik.validateForm()
                }, 0)
              }}
              error={!!(formik.touched.viewableUntil && formik.errors.viewableUntil)}
              helperText={
                formik.touched.viewableUntil && formik.errors.viewableUntil
                  ? formik.errors.viewableUntil
                  : ''
              }
              onBlur={() => formik.setFieldTouched('viewableUntil', true)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="is24hrFormat"
                  onChange={(e) => {
                    formik.setFieldValue('is24hrFormat', e.target.checked)
                    formik.setFieldTouched('is24hrFormat', true)
                  }}
                  checked={formik.values.is24hrFormat}
                  color="primary"
                  disabled={isReadOnly}
                />
              }
              label="Use 24hr Format"
            />
          </Grid>
        </Grid>

        <Box className={classes.actions}>
          <ProgressButton
            type="submit"
            label={isCreateMode ? 'Create Conference' : 'Save Changes'}
            inProgress={loading}
            disabled={loading || isReadOnly}
            color="primary"
            variant="contained"
          />
        </Box>
      </form>
    </Paper>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  subheading: {
    fontWeight: 600
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px'
  }
})

export default BasicInformationForm
